import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { type ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { RootState } from 'store';
import { logger } from 'lib/logger';
import { getProfilingWizardStatus } from 'services/navigation-wizard/getProfilingWizardStatus';
import { resolveWizardTargetOrigin, sendPostMessageToParent } from 'utils/browserUtils';
import { WizardPostMessage, WizardPostMessages, WizardStatusPayload } from 'types/postMessageTypes';

export const PROFILING_WIZARD_STATUS_QUERY_KEY = 'profilingWizardStatus';

export const getProfilingWizardStatusQueryKey = (
  brUid: string | undefined,
  serviceAgreements: ServiceAgreementV2[] | undefined,
) => [PROFILING_WIZARD_STATUS_QUERY_KEY, brUid, serviceAgreements] as const;

const publishStatus = (targetOrigin: string, payload: WizardStatusPayload) => {
  try {
    sendPostMessageToParent(WizardPostMessages.PCO_WIZARD_STATUS, targetOrigin, payload);
  } catch (error) {
    logger.warn('Failed to send the profiling wizard status', { targetOrigin }, error);
  }
};

/**
 * Loads the profiling wizard status for the navigation wizard section, which renders in the embedding CLM page and has no backend access of its own.
 */
export const useProfilingWizardStatus = (
  brUid: string | undefined,
  serviceAgreements: ServiceAgreementV2[] | undefined,
) => {
  const clmUrl = useSelector((state: RootState) => state.appConfig.CLM_URL);
  const env = useSelector((state: RootState) => state.appConfig.env);
  const userId = useSelector((state: RootState) => state.appConfig.userId);
  const targetOrigin = resolveWizardTargetOrigin(env, clmUrl);

  const { data, dataUpdatedAt, isError } = useQuery({
    queryKey: getProfilingWizardStatusQueryKey(brUid, serviceAgreements),
    queryFn: () => {
      if (!brUid) {
        throw new Error('brUid is required');
      }

      return getProfilingWizardStatus(brUid);
    },
    enabled: !!brUid && !!serviceAgreements,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const payload = useMemo<WizardStatusPayload | undefined>(
    () =>
      isError
        ? { hasError: true, userId: userId ?? undefined }
        : data && dataUpdatedAt
          ? {
              hasError: false,
              isComplete: data.isComplete,
              checklist: data.checklist,
              userId: userId ?? undefined,
            }
          : undefined,
    [data, dataUpdatedAt, isError, userId],
  );

  useEffect(() => {
    if (!targetOrigin || !payload) {
      return;
    }

    publishStatus(targetOrigin, payload);
  }, [payload, targetOrigin]);

  useEffect(() => {
    if (!targetOrigin) {
      return;
    }

    const handleRequest = (event: MessageEvent<WizardPostMessage>) => {
      if (event.data?.message !== WizardPostMessages.PCO_WIZARD_STATUS_REQUEST || !payload) {
        return;
      }

      publishStatus(targetOrigin, payload);
    };

    window.addEventListener('message', handleRequest, false);
    return () => {
      window.removeEventListener('message', handleRequest);
    };
  }, [payload, targetOrigin]);
};
